import { ComponentFactory, EventEmitter, Injectable, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { CFCustomElement, WC_PREFIXES } from './types';

@Injectable()
export class WebComponentBridgeService {
  private static readonly WC_BRIDGE_INPUT_ATTR = '_cf_set_inputs_';
  private subscriptions: Subscription[] = [];

  constructor(private zone: NgZone) {}

  public initRemoteCommunicationInterface(
    hostEl: HTMLElement,
    componentFactory: ComponentFactory<any>,
    component: any,
    remoteOrComponentName: string
  ): void {
    this.initInputs(hostEl, componentFactory, component);
    this.initOutputs(hostEl, componentFactory, component, remoteOrComponentName);
    this.initReady(hostEl, component);
  }

  public clearSubscriptions(): void {
    this.subscriptions.forEach((it) => {
      it.unsubscribe();
    });
    this.subscriptions = [];
  }

  public getSubscriptions(): Subscription[] {
    return this.subscriptions;
  }

  private initInputs(hostEl: HTMLElement, cmpFactory: ComponentFactory<any>, component: any) {
    const inputList = cmpFactory.inputs.map((it) => it.propName);
    for (let i = 0, l = hostEl.attributes.length; i < l; i++) {
      const attrItem: Attr | null = hostEl.attributes.item(i);
      if (attrItem?.name && attrItem?.name.startsWith(WC_PREFIXES.INPUT)) {
        const attr = attrItem.name.replace(`${WC_PREFIXES.INPUT}-`, '');
        if (inputList.includes(attr)) {
          component[attr] = attrItem.value;
        }
      }
    }
    // attach custom function to web component so you can set other types
    // than just strings.
    this.initElementInputFunction(hostEl, inputList, component);
  }

  private initElementInputFunction(hostEl: HTMLElement, inputList: string[], component: any) {
    // push any data that has been set on init
    const data: { [key: string]: any } = (hostEl as CFCustomElement)._cf_set_inputs_ || {};
    this.initComponentData(data, inputList, component);
    const zone = this.zone;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that: WebComponentBridgeService = this;
    if (!Object.hasOwnProperty.call(hostEl, WebComponentBridgeService.WC_BRIDGE_INPUT_ATTR)) {
      // convert the property to catch any future updates by adding a setter
      Object.defineProperty(hostEl, '_cf_set_inputs_', {
        get: function _cf_set_inputs_() {
          this.value;
        },
        set: function _cf_set_inputs_(val: any) {
          this.value = val;
          zone.run(() => {
            that.initComponentData(val, inputList, component);
          });
        },
      });
    }
  }

  private initComponentData(data: { [key: string]: any }, inputList: string[], component: any) {
    Object.keys(data).forEach((key) => {
      const newValue = data[key];
      const previousValue = component[key];
      if (inputList.includes(key) && newValue !== previousValue) {
        component[key] = data[key];
      }
    });
  }

  private initOutputs(hostEl: HTMLElement, cmpFactory: ComponentFactory<any>, component: any, remoteOrComponentName: string): void {
    cmpFactory.outputs.forEach((it) => {
      const outputName: string = it.propName;
      const eventEmitter: EventEmitter<any> = component[outputName];
      const sub = eventEmitter.subscribe((data: any) => {
        const customEvent = new CustomEvent(`${WC_PREFIXES.CUSTOM_EVENT}-${remoteOrComponentName}-${outputName}`, { detail: data });
        hostEl.dispatchEvent(customEvent);
      });
      this.subscriptions.push(sub);
    });
  }

  private initReady(hostEl: HTMLElement, component: any): void {
    hostEl.dispatchEvent(new CustomEvent(WC_PREFIXES.CUSTOM_EVENT_INIT, { detail: component }));
  }
}
